import { useState } from 'react'
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Controller } from 'react-hook-form'
import { SELECT_SEARCHABLE_MIN_OPTIONS } from '../../consts/form-select-search'
import { getFormFieldControlLabelSx } from '../../consts/form-field-layout'
import { FormFieldLabel } from './form-field-label'
import FormSelectSearchDialog from './form-select-search-dialog'
import { resolveConfigDisplayLabel, resolveOptionDisplayLabel } from '../../utils/config-localization'

/**
 * @param {{ value?: string | number, label?: string }} opt
 */
function isSelectOptionGroupHeader(opt) {
  return Boolean(opt.value && opt.value % 100 === 0)
}

/**
 * @param {Array<{ value?: string | number, label?: string }>} options
 */
export function getSelectableFormOptions(options = []) {
  return options.filter((opt) => !isSelectOptionGroupHeader(opt))
}

/**
 * @param {Array<{ value?: string | number, label?: string }>} options
 * @param {number} [minOptions]
 */
export function shouldUseSearchableFormSelect(
  options = [],
  minOptions = SELECT_SEARCHABLE_MIN_OPTIONS,
) {
  return getSelectableFormOptions(options).length > minOptions
}

/**
 * @param {unknown} a
 * @param {unknown} b
 */
function optionValueEquals(a, b) {
  return a === b || String(a) === String(b)
}

/**
 * @param {object} props
 */
function MobileSearchableSelectField({
  field,
  elem,
  selectableOptions,
  fieldsReadOnly,
  labelContent,
  labelProps,
  isCountryField,
}) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const selectedOption =
    selectableOptions.find((opt) => optionValueEquals(opt.value, field.value)) ?? null
  const displayValue = selectedOption
    ? String(resolveOptionDisplayLabel(selectedOption) ?? '')
    : ''

  return (
    <>
      <TextField
        fullWidth
        label={labelContent}
        required={elem.isRequired}
        value={displayValue}
        disabled={fieldsReadOnly}
        onClick={() => {
          if (!fieldsReadOnly) {
            setDialogOpen(true)
          }
        }}
        InputLabelProps={{
          shrink: labelProps?.shrink ?? true,
          sx: {
            ...(labelProps?.sx ?? {}),
            overflow: 'visible',
          },
        }}
        inputProps={{ readOnly: true }}
        onFocus={(e) => e.target.blur()}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon />
            </InputAdornment>
          ),
          startAdornment: elem.unit ? (
            <InputAdornment position="start">{elem.unit}&nbsp;</InputAdornment>
          ) : undefined,
        }}
        sx={{ cursor: fieldsReadOnly ? 'default' : 'pointer' }}
      />
      <FormSelectSearchDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={resolveConfigDisplayLabel(elem)}
        options={selectableOptions}
        selectedValue={field.value}
        onSelect={(opt) => field.onChange(opt?.value ?? '')}
        isCountryField={isCountryField(field.name)}
        optionValueEquals={optionValueEquals}
      />
    </>
  )
}

/**
 * @param {object} props
 * @param {string} props.name
 * @param {object} props.control
 * @param {object} props.elem
 * @param {string} props.formPath
 * @param {boolean} props.fieldsReadOnly
 * @param {import('@mui/material').Theme} props.theme
 * @param {import('@mui/material').SxProps} [props.cellSx]
 * @param {import('@mui/material').InputLabelProps} [props.labelProps]
 * @param {boolean} [props.isMobile]
 */
export default function FormSelectField({
  name,
  control,
  elem,
  formPath,
  fieldsReadOnly,
  theme,
  cellSx,
  labelProps,
  isMobile = false,
}) {
  const selectableOptions = getSelectableFormOptions(elem.options)
  const useSearchable = shouldUseSearchableFormSelect(elem.options)

  const labelContent = (
    <FormFieldLabel
      label={resolveConfigDisplayLabel(elem)}
      tooltip={elem.tooltip}
      isMobile={isMobile}
      theme={theme}
    />
  )

  const isCountryField = (fieldName) =>
    fieldName.includes('Nationality') || fieldName.includes('country')

  return (
    <FormControl
      key={`${formPath}-${elem.type}-${elem.label}`}
      fullWidth
      sx={{
        overflow: 'visible',
        ...getFormFieldControlLabelSx(isMobile),
        ...cellSx,
      }}
    >
      {!useSearchable && (
        <InputLabel
          id={`select-${elem.label}`}
          shrink={labelProps?.shrink ?? true}
          sx={labelProps?.sx ?? { overflow: 'visible' }}
        >
          {labelContent}
        </InputLabel>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (useSearchable && isMobile) {
            return (
              <MobileSearchableSelectField
                field={field}
                elem={elem}
                selectableOptions={selectableOptions}
                fieldsReadOnly={fieldsReadOnly}
                labelContent={labelContent}
                labelProps={labelProps}
                isCountryField={isCountryField}
              />
            )
          }

          if (useSearchable) {
            const selectedOption =
              selectableOptions.find((opt) => optionValueEquals(opt.value, field.value)) ?? null

            return (
              <Autocomplete
                id={`select-search-${elem.label}`}
                fullWidth
                options={selectableOptions}
                value={selectedOption}
                disabled={fieldsReadOnly}
                getOptionLabel={(opt) => String(resolveOptionDisplayLabel(opt) ?? '')}
                isOptionEqualToValue={(opt, val) =>
                  optionValueEquals(opt?.value, val?.value)
                }
                slotProps={{
                  popper: {
                    sx: { zIndex: theme.zIndex.modal + 2 },
                  },
                }}
                onChange={(_, newOption) => {
                  field.onChange(newOption?.value ?? '')
                }}
                renderOption={(props, opt) => {
                  const { key, ...optionProps } = props
                  if (isCountryField(field.name)) {
                    return (
                      <Box component="li" key={key} {...optionProps} display="flex" alignItems="center">
                        {opt.value !== 0 && (
                          <Box
                            height="1.5rem"
                            border="1px solid #c4c4c4"
                            borderRadius={1}
                            sx={{
                              aspectRatio: '4/3',
                              backgroundImage: `url('${opt.flag}')`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center center',
                            }}
                          />
                        )}
                        <Typography ml={opt.value !== 0 ? 1.5 : 0}>{resolveOptionDisplayLabel(opt)}</Typography>
                      </Box>
                    )
                  }
                  return (
                    <Box component="li" key={key} {...optionProps}>
                      {resolveOptionDisplayLabel(opt)}
                    </Box>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={labelContent}
                    required={elem.isRequired}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      ...labelProps,
                      shrink: labelProps?.shrink ?? true,
                      sx: {
                        ...(labelProps?.sx ?? {}),
                        overflow: 'visible',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {elem.unit && (
                            <InputAdornment position="start">{elem.unit}&nbsp;</InputAdornment>
                          )}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )
          }

          return (
            <Select
              {...field}
              labelId={`select-${elem.label}`}
              id={`select-${elem.label}`}
              label={labelContent}
              value={field.value}
              disabled={fieldsReadOnly}
              onChange={field.onChange}
              notched
              startAdornment={
                elem.unit && (
                  <InputAdornment position="start">{elem.unit}&nbsp;</InputAdornment>
                )
              }
            >
              {elem.options.map((opt) => {
                if (isCountryField(field.name)) {
                  return (
                    <MenuItem key={opt.label} value={opt.value}>
                      <Box display="flex">
                        {opt.value !== 0 && (
                          <Box
                            height="1.5rem"
                            border="1px solid #c4c4c4"
                            borderRadius={1}
                            sx={{
                              aspectRatio: '4/3',
                              backgroundImage: `url('${opt.flag}')`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center center',
                            }}
                          />
                        )}
                        <Typography ml={opt.value !== 0 ? 3 : 0}>{resolveOptionDisplayLabel(opt)}</Typography>
                      </Box>
                    </MenuItem>
                  )
                }
                return isSelectOptionGroupHeader(opt) ? (
                  <ListSubheader key={`${opt.label}-sub`}>{resolveOptionDisplayLabel(opt)}</ListSubheader>
                ) : (
                  <MenuItem key={opt.label} value={opt.value}>
                    {resolveOptionDisplayLabel(opt)}
                  </MenuItem>
                )
              })}
            </Select>
          )
        }}
      />
    </FormControl>
  )
}
